import { defineStore } from "pinia";
import { ref } from "vue";
import router from "@/router";
import http, { clearSession, setSession } from "@/api/http";
import { forgetKelo, mountKelo, unmountKelo } from "@/helpers/kelo";
import type { User } from "@/types/auth";

/** Signing out in one tab tells the browser's other tabs through this key. */
export const SIGNED_OUT_KEY = "rac:signed-out";

// Before RCS-149 each tab kept its own token here; drop any left behind.
try {
  sessionStorage.removeItem("RAC_ACCESS_TOKEN");
} catch {
  // Storage blocked: nothing was kept.
}

export const useAuthStore = defineStore("auth_store", () => {
  // User is never persisted to storage: it must always come fresh from
  // GET /auth/me so the app reflects the server's view of who is logged
  // in (roles/permissions/profile changes) on every reload. The sign-in
  // itself is an HttpOnly cookie the API sets (RCS-149): every tab shares
  // it, and the SPA never sees the token.
  const user = ref<User | null>(null);

  // The user payload carries the role and permission names the menu, route
  // guard and v-can buttons read (via the http session). The Kelo AI
  // assistant follows the signed-in user too.
  const setUser = (newUser: User | null) => {
    user.value = newUser;
    if (newUser) {
      setSession({ user: newUser, permissions: newUser.permissions, roles: newUser.roles });
      if (newUser.id) mountKelo(newUser.id);
    } else {
      clearSession();
      unmountKelo();
    }
  };

  const fetchUser = async () => {
    try {
      const response = await http().get("auth/me");
      setUser(response?.data?.data ?? null);
    } catch (err) {
      setUser(null);
      throw err;
    }
    return user.value;
  };

  // Sign-in finished: the API has already set the session cookie.
  const saveSession = (newUser: User) => {
    setUser(newUser);
  };

  // Also forgets the Kelo chats this browser kept.
  const endSession = () => {
    setUser(null);
    forgetKelo();
    router.push({ name: "auth.login" });
  };

  // Signing out: the API has revoked the token and cleared the cookie, which
  // every tab shares, so the other tabs are told to end their session too.
  const removeSession = () => {
    endSession();
    try {
      localStorage.setItem(SIGNED_OUT_KEY, String(Date.now()));
    } catch {
      // Storage blocked: other tabs find out on their next request.
    }
  };

  // Another tab signed out, which ended this tab's sign-in as well.
  const signedOutElsewhere = () => {
    if (user.value) endSession();
  };

  const isAuthenticated = () => user.value != null;

  return {
    user,
    fetchUser,
    saveSession,
    removeSession,
    signedOutElsewhere,
    isAuthenticated,
  };
});
